import "./create-scheduling-link-form";
import "@tento-ui/components/flow-container";
import "@tento-ui/components/icon-button";
import "@tento-ui/components/sidebar/sidebar-section-nav";

import { html, LonaWebComponent } from "@tento-ui/component";
import { Column } from "@tento-ui/component-builtin";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { FlowContainer } from "@tento-ui/components/flow-container";
import { IconButton } from "@tento-ui/components/icon-button";
import { Round } from "@tento-ui/components/round";
import { SidebarSectionNav } from "@tento-ui/components/sidebar/sidebar-section-nav";
import { LonaIcons, PlusIcon } from "@tento-ui/ui/icons";
import { Typography } from "@tento-ui/ui/typography";
import type { SchedulingLinks } from "@tento-lona";
import { CreateSchedulingLinkForm } from "./create-scheduling-link-form";
import { SchedulingLinkCell } from "./scheduling-link-cell";
import type { SchedulingLinkStore } from "./scheduling-link-store";

type PageInfo =
  | { type: "list" }
  | { type: "form"; link?: SchedulingLinks.Row };

@component({
  name: "lona-scheduling-link-sidebar",
})
export class SchedulingLinkSidebar extends LonaWebComponent {
  private $flow: FlowContainer<PageInfo> = this.hydrate("flow");
  private $nav: SidebarSectionNav = this.hydrate("header");
  private $addBtn: IconButton = IconButton.makeWith({
    content: LonaIcons.make(PlusIcon, { sizePx: 16 }),
    sizePx: 28,
  });

  private store: Option<SchedulingLinkStore>;
  private onSelect: Option<(link: SchedulingLinks.Row) => void>;
  private onEditTimes: Option<(link: SchedulingLinks.Row) => void>;
  private currentPage: PageInfo["type"] = "list";

  bind(
    store: SchedulingLinkStore,
    onSelect: (link: SchedulingLinks.Row) => void,
    onEditTimes?: (link: SchedulingLinks.Row) => void,
  ): this {
    this.store = store;
    this.onSelect = onSelect;
    this.onEditTimes = onEditTimes ?? null;
    store.ensureLoaded().then(() => {
      if (this.store === store && this.currentPage === "list") {
        this.rerenderList();
      }
    });

    this.$nav.setCallbacks({
      onBackButtonClick: () => {
        if (this.$flow.numPages <= 1) return;
        this.$flow.popPage();
      },
    });

    this.$nav.bindAccessory({
      $element: this.$addBtn,
      onClick: () => this.pushForm(),
    });

    this.$flow.onPageInfoChange = (info) => {
      const page = info?.type ?? "list";
      this.currentPage = page;
      this.$nav.toggleBackButton(page !== "list", true);
      this.$addBtn.assignStyles({
        display: page === "list" ? "" : "none",
      });
      if (page === "list") {
        this.$("title").textContent = "Scheduling Links";
      } else if (info?.type === "form") {
        this.$("title").textContent = info.link
          ? `Edit: ${info.link.label ?? info.link.attributes.slug}`
          : "New Link";
      }
    };

    const $listPage = this.buildListPage();
    this.$flow.setSizing({ mode: "fill" });
    this.$flow.reset();
    this.$flow.pushPage($listPage, { type: "list" }, false);
    this.$flow.render(null, true);

    return this;
  }

  private buildListPage(): HTMLElement {
    if (!this.store || !this.onSelect) return Column.make();

    const links = this.store.peekAll();
    if (links.length === 0) return this.buildNullState();

    const list = Column.make();
    for (const link of links) {
      const captured = link;
      list.appendChild(
        SchedulingLinkCell.makeWith({
          link: captured,
          onSelect: () => this.onSelect!(captured),
          onEdit: () => this.pushForm(captured),
        }),
      );
    }
    return list;
  }

  private buildNullState(): HTMLElement {
    const $title = Typography.h3("No scheduling links");
    const $caption = Typography.p(
      "Create a link to let others book time with you",
      "caption",
    );
    $caption.assignStyles({ color: "var(--text-sec)" });

    const $cta = Button.makeWithLight("Create Link", {
      type: "primary",
      onClick: () => this.pushForm(),
    });

    const $col = Column.make();
    $col.assignStyles({
      alignItems: "center",
      padding: "32px 16px",
      gap: "12px",
      width: "100%",
    });
    $col.appendChild($title);
    $col.appendChild($caption);
    $col.appendChild($cta);

    return Round.makeWithContent($col, Round.Props.FLAT_CARD);
  }

  private pushForm(link?: SchedulingLinks.Row) {
    if (!this.store) return;

    const $form = CreateSchedulingLinkForm.make();
    $form.toggleAttribute("inline", true);
    $form.bind({
      store: this.store,
      link,
      onEditTimes: this.onEditTimes ?? undefined,
      onSaved: (savedLink) => {
        this.$flow.popPage();
        this.rerenderList();
        this.onSelect?.(savedLink);
      },
      onDismiss: () => {
        this.$flow.popPage();
      },
    });

    this.$flow.pushPage($form, { type: "form", link });
  }

  private rerenderList() {
    this.$flow.reset();
    this.currentPage = "list";
    const $listPage = this.buildListPage();
    this.$flow.pushPage($listPage, { type: "list" }, false);
    this.$flow.render(null, true);
  }

  static override $styles: Style.Compat[] = [
    Button.$style,
    css`
      :host {
        height: 100%;
        width: 100%;
        padding-bottom: 60px;
      }

      #root {
        height: calc(100% - 24px);
        width: calc(100% - 24px);
        margin: 12px;
        border-radius: 8px;
      }

      #flow {
        --flow-bg: transparent;
        --flow-shadow: none;
      }

      #header {
        --back-btn-margin-left: 4px;
        --back-btn-margin-block: 4px;
      }

      #header-content {
        padding-inline: 8px;
        padding-block: 12px;
        justify-content: center;
      }
    `,
  ];

  static override $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <std-flow-container id="flow">
        <std-col slot="header">
          <std-sidebar-section-nav id="header">
            <std-col id="header-content" slot="title">
              <h1 id="title" class="h5">Scheduling Links</h1>
            </std-col>
          </std-sidebar-section-nav>
        </std-col>
      </std-flow-container>
    </std-col>
  `;
}
